import { get, isEmpty, map } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFolder,
  faChevronRight,
  faSpinner,
} from "@fortawesome/pro-solid-svg-icons";
import { useFetchFolderMetadata } from "@/components/files/api.tsx";
import { navigate } from "@/helpers/tixxt-router";
import classNames from "classnames";
import { Folder } from "@/@types/files.ts";

type BreadcrumbArgs = {
  folder?: Folder;
  folderId?: string;
  namespace?: string;
  contextUrl?: string;
  activeItemName?: string;
  handleNavigate?: (folder: { id: string }) => void;
};

export default function Breadcrumb({
  folder,
  folderId,
  namespace,
  contextUrl = "",
  activeItemName,
  handleNavigate,
}: BreadcrumbArgs) {
  const { data: currentFolder, isLoading } = useFetchFolderMetadata(
    folderId,
    namespace,
  );

  const actualFolder = folder || currentFolder;

  if (isLoading) return <FontAwesomeIcon icon={faSpinner} spin />;

  return (
    <ul
      className={classNames("breadcrumb", {
        "border-box mb-3 px-3 py-2 flex gap-3 align-center overflow-x-auto":
          !window.isApp,
        "flex flex-nowrap gap-2 pb-5 items-center min-w-0 overflow-hidden":
          window.isApp,
      })}
    >
      {map(get(actualFolder, ["folder_path"], []), (parentFolder, i) => (
        <li
          key={parentFolder.id}
          className={classNames("whitespace-nowrap", {
            "flex items-center justify-center gap-2 min-w-0": window.isApp,
          })}
        >
          <a
            href={
              handleNavigate
                ? "#"
                : `${contextUrl}/files/folders/${parentFolder.id}`
            }
            onClick={(e) => {
              e.preventDefault();
              if (handleNavigate) {
                handleNavigate({ id: parentFolder.id });
              } else {
                navigate(`${contextUrl}/files/folders/${parentFolder.id}`, {
                  replace: true,
                });
              }
            }}
            className={classNames({
              "mr-3": !window.isApp,
              "h-11 min-w-11 text-lg flex items-center justify-center min-w-0":
                window.isApp,
            })}
          >
            {i === 0 ? (
              <FontAwesomeIcon
                icon={faFolder}
                size={window.isApp ? "lg" : undefined}
              />
            ) : window.isApp ? (
              <span className="truncate max-w-[40vw]">{parentFolder.name}</span>
            ) : (
              parentFolder.name
            )}
          </a>
          <FontAwesomeIcon
            icon={faChevronRight}
            className={classNames("text-muted", { "shrink-0": window.isApp })}
            size={window.isApp ? "lg" : "sm"}
          />
        </li>
      ))}
      {actualFolder ? (
        activeItemName ? (
          <>
            <li
              className={classNames("whitespace-nowrap", {
                "flex items-center justify-center gap-2 min-w-0": window.isApp,
              })}
            >
              <a
                href={
                  handleNavigate
                    ? "#"
                    : `${contextUrl}/files/folders/${actualFolder.id}`
                }
                onClick={(e) => {
                  e.preventDefault();
                  if (handleNavigate) {
                    handleNavigate({ id: actualFolder.id });
                  } else {
                    navigate(`${contextUrl}/files/folders/${actualFolder.id}`, {
                      replace: true,
                    });
                  }
                }}
                className={classNames({
                  "mr-3": !window.isApp,
                  "h-11 min-w-11 text-lg flex items-center justify-center min-w-0":
                    window.isApp,
                })}
              >
                {isEmpty(get(actualFolder, ["folder_path"])) ? (
                  <FontAwesomeIcon
                    icon={faFolder}
                    size={window.isApp ? "lg" : undefined}
                  />
                ) : window.isApp ? (
                  <span className="truncate max-w-[40vw]">
                    {actualFolder.name}
                  </span>
                ) : (
                  actualFolder.name
                )}
              </a>
              <FontAwesomeIcon
                icon={faChevronRight}
                className={classNames("text-muted", {
                  "shrink-0": window.isApp,
                })}
                size={window.isApp ? "lg" : "sm"}
              />
            </li>
            <li
              className={classNames("active text-muted", {
                "h-11 text-lg flex items-center min-w-0 flex-1": window.isApp,
              })}
            >
              {window.isApp ? (
                <span className="truncate">{activeItemName}</span>
              ) : (
                activeItemName
              )}
            </li>
          </>
        ) : (
          <li
            className={classNames("active text-muted", {
              "h-11 text-lg flex items-center justify-center": window.isApp,
            })}
          >
            {isEmpty(get(actualFolder, ["folder_path"])) && (
              <FontAwesomeIcon icon={faFolder} className="mr-2" />
            )}
            {actualFolder.name}
          </li>
        )
      ) : (
        <li>
          <FontAwesomeIcon icon={faSpinner} spin />
        </li>
      )}
    </ul>
  );
}
